import React, { useState } from 'react';
import { MapPinIcon, MailIcon, PhoneIcon, GlobeIcon, SendIcon } from 'lucide-react';
type FormState = {
  name: string;
  email: string;
  message: string;
};
type FormErrors = Partial<FormState>;
export function Contact() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required.';
    if (!form.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!form.message.trim()) newErrors.message = 'Message is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setForm({
        name: '',
        email: '',
        message: ''
      });
    }, 1200);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  return <section id="contact" className="py-24 bg-white" aria-labelledby="contact-title" data-id="element-26">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-27">
        {/* Header */}
        <div className="text-center mb-14" data-id="element-28">
          <p className="section-label" data-id="element-29">Get In Touch</p>
          <h2 id="contact-title" className="section-title" data-id="element-30">
            Contact RR Group Consulting
          </h2>
          <p className="text-[#6B6B6B] max-w-xl mx-auto text-base leading-relaxed" data-id="element-31">
            Ready to take your business to the next level? Reach out to schedule
            a consultation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-32">
          {/* Left: Company Info */}
          <div data-id="element-33">
            <div className="mb-8" data-id="element-34">
              <h3 className="text-xl font-bold text-[#3C3C3C]" data-id="element-35">
                RR Group Consulting
              </h3>
              <p className="text-[#6B6B6B] font-medium mt-1" data-id="element-36">
                Rodriguez Rangel Consulting LLC
              </p>
            </div>

            <div className="space-y-5" data-id="element-37">
              <div className="flex items-start gap-4" data-id="element-38">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#3A9A6A1A'
              }} data-id="element-39">
                  <MapPinIcon size={18} color="#3A9A6A" aria-hidden="true" data-id="element-40" />
                </div>
                <div data-id="element-41">
                  <p className="text-sm font-semibold text-[#3C3C3C] mb-0.5" data-id="element-42">
                    Address
                  </p>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed" data-id="element-43">
                    8608 Abbotsbury Drive
                    <br data-id="element-44" />
                    Windermere, Florida 34786
                    <br data-id="element-45" />
                    United States
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-46">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#3A9A6A1A'
              }} data-id="element-47">
                  <MailIcon size={18} color="#3A9A6A" aria-hidden="true" data-id="element-48" />
                </div>
                <div data-id="element-49">
                  <p className="text-sm font-semibold text-[#3C3C3C] mb-0.5" data-id="element-50">
                    Email
                  </p>
                  <a href="mailto:info@rrgroupconsulting.com" className="text-[#3A9A6A] text-sm hover:underline" data-id="element-51">
                    info@rrgroupconsulting.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-52">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#3A9A6A1A'
              }} data-id="element-53">
                  <PhoneIcon size={18} color="#3A9A6A" aria-hidden="true" data-id="element-54" />
                </div>
                <div data-id="element-55">
                  <p className="text-sm font-semibold text-[#3C3C3C] mb-0.5" data-id="element-56">
                    Phone / WhatsApp
                  </p>
                  <a href="tel:+16892464375" className="text-[#3A9A6A] text-sm hover:underline" data-id="element-57">
                    +1 689 246 4375
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-58">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#3A9A6A1A'
              }} data-id="element-59">
                  <GlobeIcon size={18} color="#3A9A6A" aria-hidden="true" data-id="element-60" />
                </div>
                <div data-id="element-61">
                  <p className="text-sm font-semibold text-[#3C3C3C] mb-0.5" data-id="element-62">
                    Website
                  </p>
                  <a href="https://www.rrgroupconsulting.com" target="_blank" rel="noopener noreferrer" className="text-[#3A9A6A] text-sm hover:underline" data-id="element-63">
                    www.rrgroupconsulting.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8" data-id="element-64">
            {submitted ? <div className="text-center py-12" data-id="element-65">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{
              backgroundColor: '#3A9A6A1A'
            }} data-id="element-66">
                  <SendIcon size={28} color="#3A9A6A" data-id="element-67" />
                </div>
                <h3 className="text-xl font-bold text-[#3C3C3C] mb-2" data-id="element-68">
                  Message Sent!
                </h3>
                <p className="text-[#6B6B6B] text-sm" data-id="element-69">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
                <button onClick={() => setSubmitted(false)} className="mt-6 text-sm text-[#3A9A6A] hover:underline font-medium" data-id="element-70">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate aria-label="Contact form" data-id="element-71">
                <h3 className="text-lg font-bold text-[#3C3C3C] mb-6" data-id="element-72">
                  Request Consultation
                </h3>

                {/* Name */}
                <div className="mb-5" data-id="element-73">
                  <label htmlFor="name" className="block text-sm font-semibold text-[#3C3C3C] mb-1.5" data-id="element-74">
                    Full Name <span className="text-red-500" data-id="element-75">*</span>
                  </label>
                  <input id="name" name="name" type="text" value={form.name} onChange={handleChange} placeholder="Your full name" className={`w-full border rounded-lg px-4 py-3 text-sm text-[#3C3C3C] placeholder-gray-400 outline-none transition-colors focus:ring-2 focus:ring-[#3A9A6A] focus:border-transparent ${errors.name ? 'border-red-400' : 'border-gray-200'}`} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} data-id="element-76" />
                  {errors.name && <p id="name-error" className="mt-1 text-xs text-red-500" role="alert" data-id="element-77">
                      {errors.name}
                    </p>}
                </div>

                {/* Email */}
                <div className="mb-5" data-id="element-78">
                  <label htmlFor="email" className="block text-sm font-semibold text-[#3C3C3C] mb-1.5" data-id="element-79">
                    Email Address <span className="text-red-500" data-id="element-80">*</span>
                  </label>
                  <input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="your@email.com" className={`w-full border rounded-lg px-4 py-3 text-sm text-[#3C3C3C] placeholder-gray-400 outline-none transition-colors focus:ring-2 focus:ring-[#3A9A6A] focus:border-transparent ${errors.email ? 'border-red-400' : 'border-gray-200'}`} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-81" />
                  {errors.email && <p id="email-error" className="mt-1 text-xs text-red-500" role="alert" data-id="element-82">
                      {errors.email}
                    </p>}
                </div>

                {/* Message */}
                <div className="mb-6" data-id="element-83">
                  <label htmlFor="message" className="block text-sm font-semibold text-[#3C3C3C] mb-1.5" data-id="element-84">
                    Message <span className="text-red-500" data-id="element-85">*</span>
                  </label>
                  <textarea id="message" name="message" rows={5} value={form.message} onChange={handleChange} placeholder="Tell us about your business and consulting needs..." className={`w-full border rounded-lg px-4 py-3 text-sm text-[#3C3C3C] placeholder-gray-400 outline-none transition-colors focus:ring-2 focus:ring-[#3A9A6A] focus:border-transparent resize-none ${errors.message ? 'border-red-400' : 'border-gray-200'}`} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'message-error' : undefined} data-id="element-86" />
                  {errors.message && <p id="message-error" className="mt-1 text-xs text-red-500" role="alert" data-id="element-87">
                      {errors.message}
                    </p>}
                </div>

                <button type="submit" disabled={submitting} className="w-full bg-[#3A9A6A] hover:bg-[#2d7a54] disabled:opacity-60 text-white font-semibold rounded-lg py-3.5 text-sm transition-colors duration-200 flex items-center justify-center gap-2" data-id="element-88">
                  {submitting ? <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-89" />
                      Sending...
                    </> : <>
                      <SendIcon size={16} aria-hidden="true" data-id="element-90" />
                      Request Consultation
                    </>}
                </button>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}